import React, { useState } from "react";
import { Swords, Plus, Minus, Trophy } from "lucide-react";
import { Button } from "./ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import { confirmedAndWaitlist } from "../lib/padel";

export const ResultDialog = ({ match, onClose, onSave }) => {
  const confirmed = match ? confirmedAndWaitlist(match).confirmed.map((p) => p.name) : [];
  const [teams, setTeams] = useState({}); // name -> "A" | "B"
  const [sets, setSets] = useState([{ a: 6, b: 0 }]);
  const [busy, setBusy] = useState(false);

  React.useEffect(() => {
    if (match) {
      const init = {};
      confirmed.forEach((n, i) => (init[n] = i < 2 ? "A" : "B"));
      setTeams(init);
      setSets([{ a: 6, b: 0 }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [match?.id]);

  if (!match) return null;

  const teamA = confirmed.filter((n) => teams[n] === "A");
  const teamB = confirmed.filter((n) => teams[n] === "B");
  const setScore = (i, side, delta) =>
    setSets((prev) => prev.map((s, idx) => (idx === i ? { ...s, [side]: Math.max(0, Math.min(9, s[side] + delta)) } : s)));
  const addSet = () => setSets((prev) => (prev.length < 3 ? [...prev, { a: 0, b: 0 }] : prev));
  const removeSet = (i) => setSets((prev) => prev.filter((_, idx) => idx !== i));

  const setsA = sets.filter((s) => s.a > s.b).length;
  const setsB = sets.filter((s) => s.b > s.a).length;
  const valid = teamA.length === 2 && teamB.length === 2 && setsA !== setsB && sets.every((s) => s.a !== s.b);

  const save = async () => {
    if (!valid) return;
    setBusy(true);
    try {
      await onSave(match.id, { team_a: teamA, team_b: teamB, sets });
      onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!match} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#121b2d] border-[#1e2c4a] text-slate-100 max-w-md max-h-[90vh] overflow-y-auto" data-testid="result-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl flex items-center gap-2">
            <Swords className="w-5 h-5 text-[#ccff00]" /> Registra risultato
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Assegna i giocatori alle squadre e inserisci i set.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Squadre</p>
            <div className="space-y-2">
              {confirmed.map((name) => (
                <div key={name} className="flex items-center justify-between gap-3 bg-slate-900/60 border border-slate-700 rounded-xl px-3 py-2">
                  <span className="text-sm font-semibold text-slate-100 truncate">{name}</span>
                  <div className="flex rounded-lg overflow-hidden border border-slate-700 shrink-0">
                    {["A", "B"].map((t) => (
                      <button
                        key={t}
                        onClick={() => setTeams((p) => ({ ...p, [name]: t }))}
                        data-testid={`team-toggle-${name}-${t}`}
                        className={`px-3 py-1 text-sm font-bold ${
                          teams[name] === t
                            ? t === "A" ? "bg-[#ccff00] text-[#090d16]" : "bg-[#00e5ff] text-[#090d16]"
                            : "bg-transparent text-slate-400"
                        }`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            {(teamA.length !== 2 || teamB.length !== 2) && (
              <p className="text-xs text-[#ff9f1c] mt-2">Servono esattamente 2 giocatori per squadra.</p>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Set (A vs B)</p>
              <button onClick={addSet} disabled={sets.length >= 3} className="text-xs text-[#ccff00] font-semibold flex items-center gap-1 disabled:opacity-40">
                <Plus className="w-3 h-3" /> Set
              </button>
            </div>
            <div className="space-y-2">
              {sets.map((s, i) => (
                <div key={i} className="flex items-center gap-2 bg-slate-900/60 border border-slate-700 rounded-xl px-3 py-2">
                  <span className="text-xs text-slate-500 w-10">Set {i + 1}</span>
                  <Stepper value={s.a} onDelta={(d) => setScore(i, "a", d)} color="#ccff00" testid={`set-${i}-a`} />
                  <span className="text-slate-500">-</span>
                  <Stepper value={s.b} onDelta={(d) => setScore(i, "b", d)} color="#00e5ff" testid={`set-${i}-b`} />
                  {sets.length > 1 && (
                    <button onClick={() => removeSet(i)} className="ml-auto text-slate-500 hover:text-[#ff4d4d]">
                      <Minus className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-xl bg-slate-900/60 border border-slate-700 px-4 py-3 flex items-center justify-center gap-2 text-sm">
            <Trophy className="w-4 h-4 text-[#ccff00]" />
            {valid ? (
              <span className="font-semibold">
                Vince la <span className={setsA > setsB ? "text-[#ccff00]" : "text-[#00e5ff]"}>Squadra {setsA > setsB ? "A" : "B"}</span> ({setsA}-{setsB})
              </span>
            ) : (
              <span className="text-slate-500">Completa squadre e set</span>
            )}
          </div>
        </div>

        <Button
          disabled={!valid || busy}
          onClick={save}
          data-testid="save-result-button"
          className="w-full bg-[#ccff00] hover:bg-[#aeea00] text-[#090d16] font-bold rounded-xl h-11 disabled:opacity-40"
        >
          {busy ? "Salvataggio..." : "Salva risultato"}
        </Button>
      </DialogContent>
    </Dialog>
  );
};

const Stepper = ({ value, onDelta, color, testid }) => (
  <div className="flex items-center gap-1.5">
    <button onClick={() => onDelta(-1)} data-testid={`${testid}-minus`} className="w-6 h-6 rounded-md bg-slate-800 text-slate-300 flex items-center justify-center">
      <Minus className="w-3 h-3" />
    </button>
    <span className="w-6 text-center font-bold" style={{ color }} data-testid={`${testid}-value`}>{value}</span>
    <button onClick={() => onDelta(1)} data-testid={`${testid}-plus`} className="w-6 h-6 rounded-md bg-slate-800 text-slate-300 flex items-center justify-center">
      <Plus className="w-3 h-3" />
    </button>
  </div>
);
